import React, { useEffect, useState } from 'react';
import { getMyCoaching,markCoachingCompleted } from '../../services/coachingAPI';
import { PlayCircle, CheckCircle, Loader2 } from 'lucide-react';

const MyCoaching = () => {
    const [assignments, setAssignments] = useState([]);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [completingId, setCompletingId] = useState(null);

    useEffect(() => {
        const fetchAssignments = async () => {
            try {
                const data = await getMyCoaching();
                setAssignments(data);
            } catch (error) {
                console.error("Error fetching assignments:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAssignments();
    }, []);

    const handleComplete = async (id) => {
        setCompletingId(id);
        try {
            await markCoachingCompleted(id);
            setAssignments(assignments.map(a => a._id === id ? { ...a, status: 'completed' } : a));
        } catch (error) {
            console.error("Error marking assignment as complete:", error);
        } finally {
            setCompletingId(null);
        }
    };

    const getYoutubeVideoId = (url) => {
        const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return match && match[2].length === 11 ? match[2] : null;
    };

    const renderVideoPlayer = (url) => {
        const videoId = getYoutubeVideoId(url);
        if (videoId) {
            return (
                <div className="relative rounded-lg overflow-hidden">
                    <iframe
                        className="w-full h-64"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title="YouTube Video"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                </div>
            );
        }
        return (
            <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-500 text-center">Invalid Video URL</p>
            </div>
        );
    };


    return (
        <div className="p-4 md:p-6 lg:p-8 bg-gray-50"> {/* Added padding and background */}
            <h1 className="text-3xl font-bold text-gray-900 mb-6">My Coaching Assignments</h1>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="border rounded-lg p-6 animate-pulse bg-white">  {/* Added padding to loading */}
                            <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                            <div className="h-48 bg-gray-200 rounded mb-4"></div>
                            <div className="h-8 bg-gray-300 rounded w-1/2"></div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {assignments.map((assignment) => (
                        <div
                            key={assignment._id}
                            className="transition-all duration-300 hover:shadow-lg hover:scale-[1.01] rounded-lg bg-white shadow-md border border-gray-200"
                        >
                            <div className="py-8 px-2 ">
                                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
                                    <PlayCircle className="w-5 h-5 text-blue-500" />
                                    {assignment.video.title}
                                </h2>
                                <p className="text-sm text-gray-600 mb-3">
                                    <a
                                        href={assignment.video.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline"
                                    >
                                        Watch on YouTube
                                    </a>
                                </p>
                                {renderVideoPlayer(assignment.video.url)}
                                <div className="mt-4 flex items-center justify-between">
                                    <span
                                        className={`px-3 py-1 rounded-full text-xs font-semibold ${assignment.status === 'completed'
                                            ? "bg-green-100 text-green-800"
                                            : "bg-blue-100 text-blue-800"
                                            }`}
                                    >
                                        Status:{" "}
                                        {assignment.status === "completed" ? (
                                            <>
                                                <CheckCircle className="w-4 h-4 mr-1 inline-block" />
                                                Completed
                                            </>
                                        ) : (
                                            "Assigned"
                                        )}
                                    </span>
                                    {assignment.status === 'assigned' && (
                                        <button
                                            onClick={() => handleComplete(assignment._id)}
                                            className="bg-green-500/20 text-green-500 hover:bg-green-500/30 hover:text-green-400 transition-colors px-4 py-2 rounded-md font-medium text-sm"
                                            disabled={completingId === assignment._id}
                                        >
                                            {completingId === assignment._id ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Marking...
                                                </>
                                            ) : (
                                                'Mark as Completed'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyCoaching;
